import { C, fonts } from "../theme.js";
import { SectionLabel } from "./primitives.jsx";
import { ARCH_BOXES } from "../content.js";

export const Architecture = ({ accent }) => (
  <section id="architecture" style={{ background: C.about, padding: "140px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 56,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SectionLabel accent={accent}>// HOW WE BUILD</SectionLabel>
          <h2
            style={{
              margin: 0,
              fontFamily: fonts.serif,
              fontSize: "clamp(36px, 4.4vw, 56px)",
              fontWeight: 500,
              color: C.fg,
              letterSpacing: "-0.01em",
              lineHeight: 1.05,
            }}
          >
            A reference{" "}
            <em style={{ color: accent, fontStyle: "italic", fontWeight: 500 }}>architecture.</em>
          </h2>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: fonts.mono,
            fontSize: 13,
            color: C.body,
            maxWidth: 360,
            lineHeight: 1.65,
          }}
        >
          The shape of every system we ship — adapted to your cloud and data.
        </p>
      </div>

      <div
        style={{
          position: "relative",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 32,
          background: C.card,
        }}
      >
        <svg viewBox="0 0 1200 420" style={{ width: "100%", height: "auto", display: "block" }}>
          <defs>
            <linearGradient id="flowGrad" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor={accent} stopOpacity="0" />
              <stop offset="50%" stopColor={accent} stopOpacity="1" />
              <stop offset="100%" stopColor={accent} stopOpacity="0" />
            </linearGradient>
            <filter id="archGlow">
              <feGaussianBlur stdDeviation="2.5" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L10 5 L0 10 z" fill={accent} />
            </marker>
          </defs>

          {["INGEST", "STORE", "ORCHESTRATE", "SERVE"].map((l, i) => (
            <text
              key={l}
              x={150 + i * 300}
              y={36}
              fontFamily="'JetBrains Mono', monospace"
              fontSize="11"
              fill={accent}
              letterSpacing="3"
              textAnchor="middle"
            >
              // {l}
            </text>
          ))}

          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1={300 * i + 0.5}
              x2={300 * i + 0.5}
              y1={50}
              y2={400}
              stroke={C.border}
              strokeWidth="1"
              strokeDasharray="2 4"
              opacity="0.7"
            />
          ))}

          {ARCH_BOXES.map((b, i) => (
            <g key={i}>
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                rx="4"
                fill={C.bg}
                stroke={C.border}
                strokeWidth="1"
              />
              <text
                x={b.x + 14}
                y={b.y + 24}
                fontFamily="'Inter', sans-serif"
                fontSize="13"
                fill={C.fg}
                fontWeight="600"
              >
                {b.t}
              </text>
              <text
                x={b.x + 14}
                y={b.y + 44}
                fontFamily="'JetBrains Mono', monospace"
                fontSize="11"
                fill={C.body}
              >
                {b.sub}
              </text>
            </g>
          ))}

          {[110, 190, 270].map((y, i) => (
            <path
              key={"a" + i}
              d={`M 240 ${y} C 280 ${y}, 320 ${i < 2 ? 150 : 230}, 360 ${i < 2 ? 150 : 230}`}
              stroke={C.borderStrong}
              strokeWidth="1.2"
              fill="none"
            />
          ))}
          {[150, 230].map((y, i) => (
            <path
              key={"b" + i}
              d={`M 540 ${y} C 580 ${y}, 620 ${110 + i * 80}, 660 ${110 + i * 80}`}
              stroke={C.borderStrong}
              strokeWidth="1.2"
              fill="none"
            />
          ))}
          <path
            d="M 540 230 C 580 230, 620 270, 660 270"
            stroke={C.borderStrong}
            strokeWidth="1.2"
            fill="none"
          />
          {[110, 190, 270].map((y, i) => (
            <path
              key={"c" + i}
              d={`M 840 ${y} C 880 ${y}, 920 ${i === 0 ? 150 : 230}, 960 ${i === 0 ? 150 : 230}`}
              stroke={C.borderStrong}
              strokeWidth="1.2"
              fill="none"
            />
          ))}

          <path
            id="spine"
            d="M 60 270 C 240 270, 240 190, 360 190 C 540 190, 540 230, 660 230 C 840 230, 840 230, 960 230"
            stroke="url(#flowGrad)"
            strokeWidth="2"
            fill="none"
            filter="url(#archGlow)"
          />

          {[0, 0.33, 0.66].map((delay, i) => (
            <circle key={i} r="3.5" fill={accent} filter="url(#archGlow)">
              <animateMotion dur="6s" begin={`${delay * 6}s`} repeatCount="indefinite">
                <mpath href="#spine" />
              </animateMotion>
            </circle>
          ))}

          <path
            d="M 1140 230 L 1180 230"
            stroke={accent}
            strokeWidth="1.5"
            fill="none"
            markerEnd="url(#arrow)"
          />
          <text
            x={1180}
            y={250}
            fontFamily="'JetBrains Mono', monospace"
            fontSize="10"
            fill={accent}
            letterSpacing="2"
            textAnchor="end"
          >
            USERS
          </text>
        </svg>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            flexWrap: "wrap",
            justifyContent: "center",
            fontFamily: fonts.mono,
            fontSize: 11,
            color: C.body,
            letterSpacing: "0.14em",
          }}
        >
          <span>● OBSERVABILITY · DATADOG / GRAFANA</span>
          <span>● IaC · TERRAFORM</span>
          <span>● CI/CD · GITHUB ACTIONS</span>
          <span>● SECRETS · VAULT</span>
        </div>
      </div>
    </div>
  </section>
);
